import { Link } from "react-router-dom";
import { ArrowUpRight, Wind } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { BookButton } from "@/components/Buttons";
import { IMAGES, SEDATION_NOTE, SITE_URL, TEAM } from "@/data";
import { useSEO } from "@/hooks/useSEO";

const initials = (name) => name.replace("Dr ", "").split(" ").filter((w) => w.length > 2).map((w) => w[0]).join("");

const Portrait = ({ member }) =>
  member.photo ? (
    <img
      src={member.photo}
      alt={`${member.name}, ${member.role} at Glebe Park Dental`}
      data-testid={`team-photo-${member.slug}`}
      className="aspect-[4/5] w-full object-cover object-top"
      loading="lazy"
    />
  ) : (
    <div data-testid={`team-photo-${member.slug}`} className="flex aspect-[4/5] w-full items-center justify-center bg-sage">
      <span className="font-serif text-7xl text-stone">{initials(member.name)}</span>
    </div>
  );

const TeamMember = ({ member, flip }) => (
  <article data-testid={`team-member-${member.slug}`} className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
    <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
      <div className="relative mx-auto max-w-sm lg:max-w-none">
        <div className="absolute -inset-3 rounded-3xl border border-stone/40" />
        <div className="relative overflow-hidden rounded-3xl bg-white shadow-2xl">
          <Portrait member={member} />
        </div>
        <div className="absolute -bottom-6 left-6 rounded-xl bg-forest px-5 py-3 shadow-xl">
          <p className="font-serif text-lg leading-tight text-cream">{member.name}</p>
          <p className="font-mono text-xs tracking-[0.22em] uppercase text-leaf">{member.role}</p>
        </div>
      </div>
    </Reveal>
    <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
      <Reveal>
        <h2 className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">{member.name}</h2>
        <p className="mt-2 font-mono text-xs tracking-[0.25em] uppercase text-leafdeep">{member.role}</p>
      </Reveal>
      <Reveal delay={0.1}>
        {member.paragraphs.map((t) => (
          <p key={t.slice(0, 30)} className="mt-5 font-sans text-base leading-relaxed text-fern sm:text-lg">{t}</p>
        ))}
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 font-mono text-xs tracking-[0.25em] uppercase text-leafmute">Special interests</p>
        <ul className="mt-3 flex flex-wrap gap-2">
          {member.interests.map((i) => (
            <li key={i} className="rounded-full border border-ink/15 px-4 py-1.5 font-sans text-xs text-ink/80 transition-colors duration-300 hover:border-leaf hover:text-leafdeep sm:text-sm">{i}</li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.2}>
        <dl className="mt-8 grid grid-cols-1 gap-4 border-t border-ink/10 pt-6 sm:grid-cols-2">
          {member.facts.map((f) => (
            <div key={f.k}>
              <dt className="font-mono text-xs tracking-[0.22em] uppercase text-leafmute">{f.k}</dt>
              <dd className="mt-0.5 font-sans text-sm text-ink">{f.v}</dd>
            </div>
          ))}
        </dl>
        <blockquote className="mt-8 border-l-2 border-leaf pl-4 font-serif text-lg italic leading-snug text-ink/80">“{member.quote}”</blockquote>
      </Reveal>
    </div>
  </article>
);

export default function AboutPage() {
  useSEO({
    title: "About Us & Our Dentists | Glebe Park Dental, Canberra City",
    description: "Meet the team at Glebe Park Dental — Dr Avantica Gupta (principal dentist) and founder Dr Peter Stejskal, who continues to see patients. Honest, unhurried dentistry next to Glebe Park since 1989.",
    path: "/about",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      url: `${SITE_URL}/about`,
      mainEntity: { "@type": "Dentist", name: "Glebe Park Dental", employee: TEAM.map((m) => ({ "@type": "Person", name: m.name, jobTitle: m.role })) },
    },
  });

  return (
    <main className="bg-cream" data-testid="about-page">
      <Nav />
      <section className="relative overflow-hidden bg-sage pb-20 pt-40 lg:pt-44">
        <img src={IMAGES.reception} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-sage/80 via-sage/95 to-sage" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <Reveal><p className="font-mono text-xs tracking-[0.3em] uppercase text-leafmute">About us</p></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 max-w-3xl font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl lg:text-6xl">
              Two dentists, one team — <span className="italic text-fern">next to the park since 1989.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl font-sans text-base leading-relaxed text-fern sm:text-lg">
              Glebe Park Dental has been looking after people in Canberra City since 1989. Dr Avantica Gupta, our principal dentist, and Dr Peter Stejskal, who founded the practice, work side by side here — same rooms, same view of the park, same phone number.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl space-y-28 px-5 sm:px-8 lg:px-16">
          {TEAM.map((m, i) => <TeamMember key={m.slug} member={m} flip={i % 2 === 1} />)}
        </div>
      </section>

      <section className="bg-sage py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-16">
          <Reveal>
            <div data-testid="about-sedation-note" className="flex flex-col gap-6 rounded-2xl bg-forest p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest text-cream"><Wind className="h-5 w-5" /></span>
                <div>
                  <p className="font-mono text-xs tracking-[0.25em] uppercase text-leaf">For anxious patients</p>
                  <p className="mt-2 max-w-xl font-sans text-sm leading-relaxed text-cream sm:text-base">{SEDATION_NOTE}</p>
                </div>
              </div>
              <Link to="/services/conscious-sedation" data-testid="about-sedation-link" className="group inline-flex shrink-0 items-center gap-2 font-sans text-sm font-medium text-leaf transition-colors hover:text-cream">
                How sedation works <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </Reveal>
          <div className="mt-16 flex flex-col items-start gap-8 lg:flex-row lg:items-center lg:justify-between">
            <Reveal><h2 className="font-serif text-3xl tracking-tight text-ink sm:text-4xl">Come and meet us <span className="italic text-fern">in person.</span></h2></Reveal>
            <Reveal delay={0.1}><BookButton testid="about-book-button" /></Reveal>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
}
